import React, { useState, useEffect } from 'react';
import {
  RiCheckLine,
  RiCloseLine,
  RiCheckboxBlankCircleLine,
  RiErrorWarningLine,
  RiFileListLine,
  RiFileTextLine,
} from 'react-icons/ri';
import { CriteriaAnalysis } from '@/types/document';
import { RiInformationLine } from 'react-icons/ri';
import { CasesApi } from '@/api';
import { ValidationBadge } from '../documents/ValidationIndicator';
import Popover from '../ui/Popover';

interface Props {
  visaType?: string;
  analysis?: CriteriaAnalysis[];
  documentsCount: number;
  documents?: { id: string; fileName: string; validationResult?: any }[];
  caseId?: string;
  className?: string;
  onRefresh?: () => void; // Callback to refresh parent data
  onDocumentClick?: (documentId: string) => void; // Callback when document is clicked
}

const CaseCriteriaAnalysis: React.FC<Props> = ({
  visaType,
  analysis = [],
  documentsCount,
  documents = [],
  caseId,
  className = '',
  onRefresh,
  onDocumentClick,
}) => {
  const total = analysis.length;
  const fullySatisfiedCount = analysis.filter(
    c =>
      c.satisfactionLevel === 'FULLY_SATISFIED' ||
      c.satisfactionLevel === 'SATISFIED'
  ).length;
  const partialCount = analysis.filter(
    c => c.satisfactionLevel === 'PARTIALLY_SATISFIED'
  ).length;

  const stateClass = (c: CriteriaAnalysis) => {
    switch (c.satisfactionLevel) {
      case 'FULLY_SATISFIED':
      case 'SATISFIED':
        return 'bg-green-50';
      case 'PARTIALLY_SATISFIED':
        // Lightened yellow (less contrast) using existing palette + opacity
        return 'bg-yellow-50/40 border border-yellow-100';
      case 'NOT_SATISFIED':
        return 'bg-gray-50';
      default:
        return 'hover:bg-gray-50';
    }
  };

  const icon = (c: CriteriaAnalysis) => {
    switch (c.satisfactionLevel) {
      case 'FULLY_SATISFIED':
      case 'SATISFIED':
        return (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100">
            <RiCheckLine className="text-sm text-green-600" />
          </span>
        );
      case 'PARTIALLY_SATISFIED':
        return (
          // Lighter icon styling
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-yellow-50 border border-yellow-100">
            <RiCheckboxBlankCircleLine className="text-sm text-yellow-500" />
          </span>
        );
      case 'NOT_SATISFIED':
        return (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100">
            <RiCloseLine className="text-sm text-gray-400" />
          </span>
        );
      default:
        return (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100">
            <RiCloseLine className="text-sm text-gray-400" />
          </span>
        );
    }
  };

  const textClass = (c: CriteriaAnalysis) => {
    switch (c.satisfactionLevel) {
      case 'FULLY_SATISFIED':
      case 'SATISFIED':
        return 'text-gray-900 font-medium';
      case 'PARTIALLY_SATISFIED':
        return 'text-gray-700';
      case 'NOT_SATISFIED':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  // Sort criteria: fully satisfied first, then partial, then not satisfied
  const sorted = [...analysis].sort((a, b) => {
    const order = (c: CriteriaAnalysis) => {
      switch (c.satisfactionLevel) {
        case 'FULLY_SATISFIED':
        case 'SATISFIED':
          return 0;
        case 'PARTIALLY_SATISFIED':
          return 1;
        case 'NOT_SATISFIED':
          return 2;
        default:
          return 3;
      }
    };
    const diff = order(a) - order(b);
    if (diff !== 0) return diff;
    return a.description.localeCompare(b.description);
  });

  // Keep expand state per-criterion stable across polling refreshes
  const [openDocsMap, setOpenDocsMap] = useState<Record<string, boolean>>({});
  const [openGapsMap, setOpenGapsMap] = useState<Record<string, boolean>>({});
  const [criteriaExpanded, setCriteriaExpanded] = useState(false);

  const toggleDocs = (id: string) =>
    setOpenDocsMap(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleGaps = (id: string) =>
    setOpenGapsMap(prev => ({ ...prev, [id]: !prev[id] }));

  // Manual assignment state
  const [assigningCriterionId, setAssigningCriterionId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const handleAssignDocument = async (documentId: string) => {
    if (!caseId || !assigningCriterionId) return;
    
    setIsAssigning(true);
    try {
      await CasesApi.assignDocumentToCriteria(caseId, documentId, assigningCriterionId);
      onRefresh?.();
      setAssigningCriterionId(null);
    } catch (error) {
      console.error('Failed to assign document:', error);
      alert('Failed to assign document to criterion');
    } finally {
      setIsAssigning(false);
    }
  };

  // Filter documents for picker
  const filteredDocuments = (documents || []).filter(d => 
    d.fileName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show empty/loading state instead of hiding completely
  if (!analysis.length || documentsCount === 0) {
    return (
      <div className={`bg-white border border-neutral-200 rounded-lg p-6 ${className}`}>
        <div className="flex flex-col items-center justify-center text-center py-8">
          <RiFileListLine className="w-12 h-12 text-neutral-300 mb-3" />
          <h3 className="text-sm font-medium text-neutral-700 mb-1">
            {documentsCount === 0 ? 'No Documents Yet' : 'Processing Documents'}
          </h3>
          <p className="text-xs text-neutral-500 max-w-sm">
            {documentsCount === 0
              ? 'Upload documents to see criteria analysis for this case.'
              : 'Documents are being processed. Criteria analysis will appear here once processing is complete.'}
          </p>
          {documentsCount > 0 && onRefresh && (
            <button
              onClick={onRefresh}
              className="mt-4 text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Refresh Analysis
            </button>
          )}
        </div>
      </div>
    );
  }

  // Stateless row; receives open state and togglers to prevent collapse on re-renders
  const CriterionRow: React.FC<{
    crit: CriteriaAnalysis;
    showDocs: boolean;
    showGaps: boolean;
    onToggleDocs: (id: string) => void;
    onToggleGaps: (id: string) => void;
  }> = ({ crit, showDocs, showGaps, onToggleDocs, onToggleGaps }) => {
    const supportingIds: string[] = Array.isArray(crit.supportingDocuments)
      ? crit.supportingDocuments
          .map(d => (typeof d === 'string' ? d : d?.documentId || ''))
          .filter(Boolean)
      : [];

    // Count only documents that are actually found in the loaded documents array
    // This prevents showing "3" when only 1 document is actually available
    const foundDocs = supportingIds.filter(docId => documents.some(d => d.id === docId));
    const foundDocsCount = foundDocs.length;

    const hasDocs =
      (crit.satisfactionLevel === 'FULLY_SATISFIED' ||
        crit.satisfactionLevel === 'SATISFIED' ||
        crit.satisfactionLevel === 'PARTIALLY_SATISFIED') &&
      foundDocsCount > 0;

    const hasGaps = Array.isArray(crit.gaps) && crit.gaps.length > 0;

    return (
      <div
        className={`flex items-start p-2 rounded transition-colors relative ${stateClass(
          crit
        )}`}
      >
        <div className="flex-shrink-0 mr-2 mt-0.5">{icon(crit)}</div>
        <div className="flex-1">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <p
                className={`text-sm ${textClass(crit)} break-words leading-relaxed`}
                title={crit.detailedDescription || crit.description}
              >
                {crit.criteriaName}
              </p>
              {crit.detailedDescription && (
                <Popover
                  trigger="hover"
                  side="right"
                  align="start"
                  className="p-3 w-80"
                  content={
                    <div className="text-xs leading-relaxed">
                      <div className="font-semibold text-gray-900 mb-1">
                        Detailed Description
                      </div>
                      <div className="text-gray-700">
                        {crit.detailedDescription}
                      </div>
                    </div>
                  }
                >
                  <RiInformationLine className="w-3 h-3 text-blue-500 cursor-pointer flex-shrink-0" />
                </Popover>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-2">
              <span className="text-xs uppercase tracking-wide font-medium text-gray-500">
                {crit.satisfactionLevel.replace('_', ' ').toLowerCase()}
              </span>
              {hasDocs && (
                <button
                  type="button"
                  onClick={() => onToggleDocs(crit.criteriaId)}
                  className="text-xs px-1.5 py-0.5 rounded bg-blue-50 border border-blue-300 text-blue-500 hover:bg-blue-100 transition-colors flex items-center gap-1"
                >
                  <RiFileListLine className="w-3 h-3" />
                  {showDocs ? 'Hide' : 'Docs'} ({foundDocsCount})
                </button>
              )}
              {hasGaps && (
                <button
                  type="button"
                  onClick={() => onToggleGaps(crit.criteriaId)}
                  title={showGaps ? 'Hide gaps' : 'Show gaps'}
                  className="text-xs px-1.5 py-0.5 rounded bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 transition-colors flex items-center gap-1"
                >
                  <RiErrorWarningLine className="w-3 h-3" />
                  {showGaps ? 'Hide' : 'Missing Evidence'} ({crit.gaps.length})
                </button>
              )}
              {/* Manual Assign Button */}
              <button
                type="button"
                onClick={() => setAssigningCriterionId(crit.criteriaId)}
                className="text-xs px-1.5 py-0.5 rounded bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <RiCheckLine className="w-3 h-3" />
                Assign
              </button>
            </div>
          </div>
          {hasGaps && showGaps && (
            <ul className="mt-2 ml-4 list-disc space-y-1 text-xs text-amber-800">
              {crit.gaps.map((gap, idx) => (
                <li key={idx} className="marker:text-amber-500">
                  {gap}
                </li>
              ))}
            </ul>
          )}
          {hasDocs && showDocs && (
            <ul className="mt-2 ml-4 list-disc space-y-1 text-xs text-gray-700">
              {supportingIds.map(docId => {
                const doc = documents.find(d => d.id === docId);
                if (!doc) return null;

                return (
                  <li
                    key={docId}
                    className="marker:text-gray-400 flex items-start gap-1 group/doc-item"
                  >
                    <span className="break-all flex-1 flex items-center gap-1.5">
                      <RiFileTextLine className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      {onDocumentClick ? (
                        <button
                          onClick={() => onDocumentClick(docId)}
                          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                        >
                          {doc.fileName}
                        </button>
                      ) : (
                        doc.fileName
                      )}
                      {doc.validationResult && (
                        <ValidationBadge
                          validation={doc.validationResult}
                          variant="icon"
                        />
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`my-6 p-4 bg-blue-50 rounded-lg border border-blue-200 ${className}`}
      >
        <button
          onClick={() => setCriteriaExpanded(!criteriaExpanded)}
          className="w-full text-left"
        >
          <h3 className="text-lg font-semibold text-blue-800 flex items-center flex-wrap justify-between">
            <div className="flex items-center flex-wrap">
              {visaType
                ? `${visaType} Visa Criteria Match`
                : 'Visa Criteria Match'}
              <span className="ml-2 px-2 py-0.5 text-sm bg-blue-200 text-blue-800 rounded-full">
                {fullySatisfiedCount}/{total}
              </span>
              {partialCount > 0 && (
                <span className="ml-2 px-2 py-0.5 text-sm bg-yellow-100 text-yellow-800 rounded-full border border-yellow-300 font-medium">
                  +{partialCount} partial
                </span>
              )}
            </div>
            <span className="text-sm text-blue-600">
              {criteriaExpanded ? 'Hide Details' : 'Show Details'}
            </span>
          </h3>
        </button>
        {criteriaExpanded && (
          <div className="space-y-2 mt-3">
            {sorted.map(crit => (
              <CriterionRow
                key={crit.criteriaId}
                crit={crit}
                showDocs={!!openDocsMap[crit.criteriaId]}
                showGaps={!!openGapsMap[crit.criteriaId]}
                onToggleDocs={toggleDocs}
                onToggleGaps={toggleGaps}
              />
            ))}
          </div>
        )}
      </div>

      {/* Document Picker Modal */}
      {assigningCriterionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Assign Document</h3>
              <button 
                onClick={() => setAssigningCriterionId(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-100">
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            <div className="overflow-y-auto flex-1 p-2">
              {filteredDocuments.length > 0 ? (
                <div className="space-y-1">
                  {filteredDocuments.map(doc => (
                    <button
                      key={doc.id}
                      onClick={() => handleAssignDocument(doc.id)}
                      disabled={isAssigning}
                      className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 flex items-center gap-2 transition-colors"
                    >
                      <RiFileTextLine className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate text-sm text-gray-700">{doc.fileName}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No documents found
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CaseCriteriaAnalysis;
